import { useState } from "react";

import clsx from "clsx";

import { FETCH_SCHEDULED_EXPORTS } from "@gql/data-export";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  type FetchScheduledExports,
  type FetchScheduledExportsVariables,
  type FetchScheduledExports_me_scheduledExports_data,
} from "@north-beam/nb-client-side";
import { SecondaryButton } from "@shared/buttons";

import { CreateScheduledExportButtonWithModal } from "./create-scheduled-export-button-with-modal";
import { CreateScheduledExportV2ButtonWithModal } from "./create-scheduled-export-v2-button-with-modal";
import { DeleteModal } from "./delete-modal";
import { TableRow } from "./table-row";

export function ManageExports() {
  const { hasFeatureFlag } = useFeatureFlags();

  const [page, setPage] = useState(1);
  const [toDeleteRow, setToDeleteRow] =
    useState<FetchScheduledExports_me_scheduledExports_data>();

  const enableExportsV2Creation = hasFeatureFlag("enableTouchpointsExport");

  const {
    data,
    loading,
    refetch: refetchScheduledExports,
  } = useNorthbeamQuery<FetchScheduledExports, FetchScheduledExportsVariables>(
    FETCH_SCHEDULED_EXPORTS,
    {
      variables: { page },
    },
  );

  if (loading) {
    return <p>Please wait...</p>;
  }

  if (!data) {
    return null;
  }

  const totalPages = data.me.scheduledExports.totalPages;
  const dataRows = data.me.scheduledExports.data;

  const tableHeaderClassName = "th-sm align-middle text-center";

  return (
    <div>
      <DeleteModal
        onClose={() => setToDeleteRow(undefined)}
        row={toDeleteRow}
      />
      <div className="flex gap-8 justify-between items-center">
        <p className="m-0">
          If you would like to delete a previously scheduled export or
          add/remove recipients please use the table below
        </p>
        {!enableExportsV2Creation && (
          <CreateScheduledExportButtonWithModal
            onSuccess={async () => {
              await refetchScheduledExports({ page: 1 });
            }}
          />
        )}
        {enableExportsV2Creation && <CreateScheduledExportV2ButtonWithModal />}
      </div>
      <div className="mt-4 row">
        <div className="overflow-x-scroll col-md-12">
          <table className="table text-sm bg-white table-bordered">
            <thead className="thead-light">
              <tr>
                <th scope="col" className={tableHeaderClassName}>
                  Created date
                </th>
                <th scope="col" className={tableHeaderClassName}>
                  Report type
                </th>
                <th scope="col" className={tableHeaderClassName}>
                  Schedule name
                </th>
                <th scope="col" className={tableHeaderClassName}>
                  File name prefix
                </th>
                <th scope="col" className={tableHeaderClassName}>
                  Cadence
                </th>
                <th
                  scope="col"
                  className={clsx(tableHeaderClassName, "w-[200px]")}
                >
                  Destination
                </th>
                <th
                  scope="col"
                  className={clsx(tableHeaderClassName, "w-[200px]")}
                >
                  Email recipients
                </th>
                <th scope="col" className={tableHeaderClassName}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {dataRows.length > 0 ? (
                dataRows.map((r) => (
                  <TableRow
                    key={r.id}
                    emailRecipients={data.me.dataExportEmailRecipients}
                    row={r}
                    onDelete={(row) => setToDeleteRow(row)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={6}>No scheduled exports have been created.</td>
                </tr>
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex gap-3 justify-center">
              <SecondaryButton
                isDisabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                &laquo; prev
              </SecondaryButton>
              <SecondaryButton
                isDisabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                next &raquo;
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
